import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions/Actions';

import { TAwtgRanDetailItem } from '@interfaces/cmModule/dto/historyCheckDTO';
import { IHistoryCheckDetail } from '@interfaces/cmModule/historyCheck';
import { TExtendedColumnDef } from '@interfaces/components/table';

import { columnSizes } from '@constants/table';

type THistoryDetailItem = IHistoryCheckDetail | TAwtgRanDetailItem;

export const getUniqueMoNames = (historyDetails: THistoryDetailItem[]): string[] => {
	const moNames = new Set<string>();

	historyDetails.forEach(detail => {
		detail.managedObjects?.forEach(mo => {
			if (mo.moName) {
				moNames.add(mo.moName);
			}
		});
	});

	return Array.from(moNames).sort();
};

export const createDynamicMoColumns = (historyDetails: THistoryDetailItem[]) => {
	const uniqueMoNames = getUniqueMoNames(historyDetails);

	return uniqueMoNames.map(moName => ({
		header: `${moName} id`,
		accessorKey: `mo_${moName}`,
		cell: ({ row }: CellContext<any, any>) => {
			const managedObjects = row.original?.managedObjects || [];
			const mo = managedObjects.find((m: any) => m.moName === moName);

			return <AdaptiveTableCell text={mo?.moId || ''} />;
		},
	}));
};

interface IActionColumns {
	openTable: () => void;
	setSelectedParameter: (data: IHistoryCheckDetail) => void;
}

export const getColumns = (isMavenirVendor: boolean, isAwtgRanVendor?: boolean) => {
	if (isAwtgRanVendor) {
		return [
			{
				header: 'Parameter',
				accessorKey: 'paramName',
				cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
			},
			{
				header: 'NE Name',
				accessorKey: 'neName',
				cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
			},
		];
	}

	return [
		{
			header: 'Parameter',
			accessorKey: 'paramName',
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Site ID',
			accessorKey: 'siteId',
			isHidden: isMavenirVendor,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'Node ID',
			accessorKey: 'gnbId',
			isHidden: !isMavenirVendor,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
		{
			header: 'CNF ID',
			accessorKey: 'configId',
			isHidden: !isMavenirVendor,
			cell: ({ getValue }: CellContext<any, any>) => <AdaptiveTableCell text={getValue()} />,
		},
	];
};

export const getActionColumn = ({
	openTable,
	setSelectedParameter,
}: IActionColumns) => ({
	header: 'Actions',
	accessorKey: 'Actions',
	enableSorting: false,
	size: columnSizes.size135,
	cell: ({ row }: CellContext<any, any>) => {
		const handleOpenTableBranch = () => {
			openTable();
			setSelectedParameter(row.original);
		};

		return (
			<Actions
				isOpenModal
				openModalHandler={handleOpenTableBranch}
			/>
		);
	},
});

export const getParametersToShow = ({
	vendorColumns,
	selectedParameter,
}: {
	vendorColumns: TExtendedColumnDef<any>[];
	selectedParameter?: THistoryDetailItem;
}) => {
	const columnsKeys: Record<string, string> = vendorColumns.reduce((acc, item) => {
		return item.accessorKey
			? { ...acc, [item.accessorKey.replace('managedObjects.', '')]: item.header }
			: acc;
	}, {});

	const parameters = selectedParameter?.managedObjects
		? {
			...selectedParameter,
			...selectedParameter.managedObjects
				.reduce((acc, mo) => {
					return { ...acc, [mo.moName]: mo.moId };
				}, {}),
		} : undefined;

	return parameters
		? Object.entries(parameters).reduce((acc, item) => {
			const [key, value] = item;

			const headerText = columnsKeys[key];

			return headerText ? { ...acc, [headerText]: value } : acc;
		}, {}) : {
			'NE Name': (selectedParameter as TAwtgRanDetailItem)?.neName || 'Site ID: ' + (selectedParameter as IHistoryCheckDetail)?.siteId,
		};
};
